/* Prices and eligibility are always read from Shopify, never overridden in the UI. */
(() => {
  const marker = 'niacinamide';
  function rewardState(cart, config) {
    const gifts = cart.items.filter(item => item.properties?._bv_reward === marker);
    const allLines = cart.items.reduce((total, item) => total + item.final_line_price, 0);
    const giftTotal = gifts.reduce((total, item) => total + item.final_line_price, 0);
    const subtotal = Math.max(0, (cart.total_price ?? allLines) - giftTotal);
    const eligible = config.enabled && cart.currency === 'PKR' && subtotal >= config.giftThreshold;
    return { gifts, subtotal, eligible };
  }
  // Expose pure eligibility logic for offline verification without a Shopify session.
  if (typeof module !== 'undefined' && module.exports) {
    module.exports = { rewardState };
    return;
  }
  const node = document.getElementById('BeavitalCartConfig');
  if (!node) return;
  const config = JSON.parse(node.textContent);
  const root = window.Shopify.routes.root;
  let task = null;
  let rerun = false;
  let internal = false;
  let mutations = 0;
  let failureSignature = null;
  let status = '';
  let checkoutPending = false;
  function signature(cart) {
    return JSON.stringify(cart.items.filter(item => item.properties?._bv_reward !== marker)
      .map(item => [item.variant_id, item.quantity, item.original_line_price]).sort());
  }
  const sleep = ms => new Promise(resolve => setTimeout(resolve, ms));

  async function request(path, body) {
    const response = await fetch(root + path, body ? {
      method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    } : { headers: { Accept: 'application/json' }, cache: 'no-store' });
    const result = await response.json();
    if (!response.ok || result.errors) throw new Error(result.description || result.message || 'Unable to update your cart.');
    return result;
  }

  function busy(active) {
    document.documentElement.classList.toggle('bv-rewards-busy', active || mutations > 0 || checkoutPending);
    document.querySelectorAll('[data-bv-rewards]').forEach(el => el.setAttribute('aria-busy', String(active || mutations > 0)));
  }

  function paintStatus() {
    document.querySelectorAll('[data-bv-reward-status]').forEach(el => { el.textContent = status; });
  }

  async function waitForMutations() {
    const deadline = Date.now() + 15000;
    while (mutations > 0) {
      if (Date.now() > deadline) throw new Error('Your cart is still updating. Please try again.');
      await sleep(50);
    }
  }

  async function refresh() {
    const sections = await request('?sections=cart-drawer,cart-template,cart-count');
    // A missing section response must not replace the working cart with empty HTML.
    if (!sections['cart-drawer'] || !sections['cart-template']) throw new Error('Please refresh to see your updated cart.');
    internal = true;
    try {
      window.MinimogEvents.emit(window.MinimogTheme.pubSubEvents.cartUpdate, { cart: { sections } });
      const count = new DOMParser().parseFromString(sections['cart-count'] || '', 'text/html');
      document.querySelectorAll('m-cart-count').forEach(el => el.onUpdate(count));
    } finally { internal = false; }
    paintStatus();
  }

  async function reconcile() {
    await waitForMutations();
    let cart = await request('cart.js');
    let changed = false;
    status = '';
    for (let attempt = 0; attempt < 10; attempt++) {
      const state = rewardState(cart, config);
      if (failureSignature !== signature(cart)) failureSignature = null;
      // Remove tagged gifts when ineligible, malformed, duplicated, or charged.
      const invalid = state.gifts.find((item, index) => !state.eligible ||
        Number(item.variant_id) !== Number(config.variantId) || index > 0 || item.final_line_price > 0);
      if (invalid) {
        if (state.eligible && invalid.final_line_price > 0) {
          failureSignature = signature(cart);
          status = 'Your serum reward is temporarily unavailable. You will not be charged for a gift.';
        }
        cart = await request('cart/change.js', { id: invalid.key, quantity: 0 });
        changed = true;
        continue;
      }
      if (state.gifts[0]?.quantity > 1) {
        cart = await request('cart/change.js', { id: state.gifts[0].key, quantity: 1 });
        changed = true;
        continue;
      }
      if (state.eligible && !state.gifts.length) {
        if (!config.variantId || !config.available || failureSignature === signature(cart)) {
          status = 'Your serum reward is temporarily unavailable. You will not be charged for a gift.';
          break;
        }
        try {
          await request('cart/add.js', { items: [{ id: config.variantId, quantity: 1, properties: { _bv_reward: marker } }] });
          cart = await request('cart.js');
          changed = true;
          continue;
        } catch (error) {
          // A failed response can still follow a successful server mutation; re-read.
          failureSignature = signature(cart);
          status = 'Your serum reward is temporarily unavailable. You will not be charged for a gift.';
          cart = await request('cart.js');
          changed = true;
          continue;
        }
      }
      break;
    }
    const final = rewardState(cart, config);
    if (final.gifts.some(item => !final.eligible || item.final_line_price > 0 || item.quantity !== 1) || final.gifts.length > 1) {
      throw new Error('Please refresh your cart before checking out.');
    }
    if (changed) await refresh();
    paintStatus();
    return cart;
  }

  function sync() {
    rerun = true;
    if (task) return task;
    busy(true);
    task = (async () => {
      let cart;
      while (rerun) { rerun = false; cart = await reconcile(); }
      return cart;
    })().catch(error => {
      status = error.message;
      paintStatus();
      throw error;
    }).finally(() => { task = null; busy(false); });
    return task;
  }
  const schedule = () => { if (!internal) sync().catch(() => {}); };
  document.addEventListener('cart:updated', schedule);
  document.addEventListener('product-ajax:added', schedule);
  document.addEventListener('bv:cart-mutation-start', () => { mutations++; busy(true); });
  document.addEventListener('bv:cart-mutation-end', () => { mutations = Math.max(0, mutations - 1); schedule(); });
  window.addEventListener('pageshow', schedule);
  window.MinimogEvents.subscribe(window.MinimogTheme.pubSubEvents.openCartDrawer, schedule);

  document.addEventListener('click', async event => {
    const couponButton = event.target.closest('.m-cart-addon--save[data-action="coupon"]');
    if (couponButton) {
      event.preventDefault();
      event.stopImmediatePropagation();
      const input = couponButton.closest('m-cart-addons').querySelector('[name="discount"]');
      const code = input.value.trim();
      couponButton.disabled = true;
      try {
        await sync();
        await request('cart/update.js', { discount: code });
        localStorage.setItem('minimog-discount-code', code);
        failureSignature = null;
        await sync();
        await refresh();
      } catch (error) { status = error.message; paintStatus(); }
      finally { couponButton.disabled = false; busy(false); }
      return;
    }
    const button = event.target.closest('[data-bv-pick]');
    if (!button || button.disabled) return;
    button.disabled = true;
    let adding = false;
    busy(true);
    try {
      await sync();
      await waitForMutations();
      mutations++;
      adding = true;
      await request('cart/add.js', { items: [{ id: Number(button.dataset.bvPick), quantity: 1 }] });
      mutations--;
      adding = false;
      await sync();
      await refresh();
    } catch (error) { status = error.message; paintStatus(); }
    finally { if (adding) mutations = Math.max(0, mutations - 1); button.disabled = false; busy(false); }
  }, true);

  // Run a final server check before ordinary cart checkout (including keyboard submit).
  document.addEventListener('submit', async event => {
    if (event.submitter?.name !== 'checkout' || !event.target.action.includes('/cart')) return;
    event.preventDefault();
    if (checkoutPending) return;
    checkoutPending = true;
    busy(true);
    try {
      const fields = new FormData(event.target);
      const updates = {};
      if (fields.has('note')) updates.note = fields.get('note');
      const attributes = {};
      for (const [name, value] of fields) {
        const match = name.match(/^attributes\[(.+)\]$/);
        if (match) attributes[match[1]] = value;
      }
      if (Object.keys(attributes).length) updates.attributes = attributes;
      const discount = fields.get('discount')?.trim();
      if (discount) updates.discount = discount;
      // Allow the theme's 300ms quantity debounce to enter its mutation lifecycle.
      await sleep(350);
      await sync();
      if (Object.keys(updates).length) {
        await request('cart/update.js', updates);
        await sync();
      }
      window.location.assign(root + 'checkout');
    } catch (error) { status = error.message; paintStatus(); }
    finally { checkoutPending = false; busy(false); }
  }, true);
  window.BeaVitalCartRewards = { sync };
  schedule();
})();
